import {
    HsabExpenseCategory,
    HsabExpenseCategoryDisplay,
    HsabPlanSubType,
    HsabPlanType,
    WexExpenseAccountType
} from "@/shared/types";
import _ from "lodash";

interface EligiblePlanCombo {
    type: HsabPlanType;
    anySubType: boolean;
    subTypes?: HsabPlanSubType[];
}

interface PlanTypesAndSubTypes {
    types: HsabPlanType[];
    subTypes: HsabPlanSubType[];
}

export function getAllDisplayCategories (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }) {
    const results: HsabExpenseCategoryDisplay[] = [];

    if (includeMedical(planTypesAndSubtypes)) {
        results.push(MedicalCategoryDisplay);
    }
    if (includeDental(planTypesAndSubtypes)) {
        results.push(DentalCategoryDisplay);
    }
    if (includeVision(planTypesAndSubtypes)) {
        results.push(VisionCategoryDisplay);
    }
    if (includePharmacy(planTypesAndSubtypes)) {
        results.push(PharmacyCategoryDisplay);
    }
    if (includePremium(planTypesAndSubtypes)) {
        results.push(PremiumCategoryDisplay);
    }
    if (includeDependentCare(planTypesAndSubtypes)) {
        results.push(DependentCareCategoryDisplay);
    }
    if (includeParking(planTypesAndSubtypes)) {
        results.push(ParkingCategoryDisplay);
    }
    if (includeMassTransit(planTypesAndSubtypes)) {
        results.push(MassTransitCategoryDisplay);
    }
    if (includeLifestyle(planTypesAndSubtypes)) {
        results.push(LifestyleCategoryDisplay);
    }
    if (includeTuition(planTypesAndSubtypes)) {
        results.push(TuitionCategoryDisplay);
    }

    return results;
}

function shouldDisplay (planTypesAndSubtypes: PlanTypesAndSubTypes, eligiblePlanCombos: EligiblePlanCombo[]): boolean {
    let result = false;

    _.forEach(eligiblePlanCombos, combo => {
        const hasType = planTypesAndSubtypes.types.some(x => x === combo.type);
        if (hasType && combo.anySubType) {
            result = true;
            return false; // break
        } else {
            const hasSubType = _.intersection(planTypesAndSubtypes.subTypes, combo.subTypes).length > 0;
            if (hasSubType) {
                result = true;
                return false; // break
            }
        }
    });

    return result;
}

export function includeMedical (planTypesAndSubtypes: PlanTypesAndSubTypes): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeDental (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: true,
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeVision (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: true,
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includePharmacy (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[]
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includePremium (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    return false;
}

export function includeDependentCare (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[]
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.DependentCare,
            anySubType: true
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeParking (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.CommuterParking,
            anySubType: true
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeMassTransit (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[]
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.CommuterTransit,
            anySubType: true
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeLifestyle (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[]
}): boolean {
    return false;
}

export function includeTuition (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    return false;
}

export const MedicalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Medical,
    external: WexExpenseAccountType.Medical,
    display: "Medical",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
    removeTypesFromSelector: ["Dental", "Vision", "Drugs & Medicine"],
    addTypesIntoSelector: [], // handled in load store method due to hsa only logic
};

export const DentalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Dental,
    external: WexExpenseAccountType.Medical,
    display: "Dental",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Dental",
    removeTypesFromSelector: [],
    addTypesIntoSelector: [{ expenseType: "Dental", expenseSubTypes: ["Other Dental"]}]
};

export const VisionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Vision,
    external: WexExpenseAccountType.Medical,
    display: "Vision",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Vision",
    removeTypesFromSelector: [],
    addTypesIntoSelector: [{ expenseType: "Vision", expenseSubTypes: ["Other Vision"]}]
};

export const PharmacyCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Pharmacy,
    external: WexExpenseAccountType.Medical,
    display: "Pharmacy",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Drugs & Medicine",
    removeTypesFromSelector: [],
    addTypesIntoSelector: [{ expenseType: "Drugs & Medicine", expenseSubTypes: ["Other Drugs & Medicine"]}]
};

export const DependentCareCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.DependentCare,
    external: WexExpenseAccountType.DependentCare,
    display: "Dependent Care",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};

export const ParkingCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Parking,
    external: WexExpenseAccountType.Transportation,
    display: "Parking",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation - Parking",
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};

export const MassTransitCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.MassTransit,
    external: WexExpenseAccountType.Transportation,
    display: "Mass Transit",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation",
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};

export const PremiumCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Premium,
    external: WexExpenseAccountType.Premium,
    display: "Premium",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};

export const LifestyleCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Lifestyle,
    external: WexExpenseAccountType.Lifestyle,
    display: "Life style",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};

export const TuitionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Tuition,
    external: WexExpenseAccountType.Tuition,
    display: "Tuition",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
    removeTypesFromSelector: [],
    addTypesIntoSelector: [],
};